// Блок 3 «Продавец-консультант» — калькуляторы 13–15 прототипа.

import { findMaterial, MATERIALS, type LensMaterial } from './constants';
import { decentrationPerEye, edgeCenterThickness, prismByPrentice, volumeWeight } from './core';
import { verticalImbalance } from './lab';

// --- 13. Вес и материалы ------------------------------------------------------

export interface WeightInput {
  diameter: number;
  power: number;
  /** Базовая толщина (центр для минуса, край для плюса), мм. */
  refThickness: number;
}

export interface MaterialWeight {
  material: LensMaterial;
  /** Средняя толщина линзы, мм. */
  averageThickness: number;
  /** Вес одной линзы, г. */
  perLens: number;
  /** Вес пары, г. */
  perPair: number;
}

/** Вес линзы в каждом материале ряда: объём ≈ цилиндр средней толщины × плотность. */
export function weightByMaterials({
  diameter,
  power,
  refThickness,
}: WeightInput): MaterialWeight[] {
  return MATERIALS.map((material) => {
    const thickness = edgeCenterThickness(diameter, power, material.index, refThickness);
    const averageThickness = (thickness.center + thickness.edge) / 2;
    const perLens = volumeWeight(diameter, averageThickness, material.density);
    return { material, averageThickness, perLens, perPair: perLens * 2 };
  });
}

// --- 14. Сравнение индексов (продажа) -----------------------------------------

export interface SellUpInput extends WeightInput {
  /** Индекс варианта A (как в справочнике материалов). */
  indexA: number | string;
  /** Цена варианта A, ₸. */
  priceA: number;
  indexB: number | string;
  priceB: number;
}

export interface SellUpVariant {
  material: LensMaterial;
  /** Значимая толщина: край для минуса, центр для плюса, мм. */
  thickness: number;
  /** Вес линзы, г. */
  weight: number;
}

export interface SellUpResult {
  a: SellUpVariant;
  b: SellUpVariant;
  /** Насколько B тоньше A, мм (знак сохранён: «+» — B тоньше). */
  thicknessDiff: number;
  /** Насколько пара B легче пары A, г (знак сохранён). */
  weightDiffPair: number;
  /** Доплата за переход A → B, ₸. */
  priceDiff: number;
}

function sellUpVariant(input: WeightInput, index: number | string): SellUpVariant {
  const material = findMaterial(index);
  const thickness = edgeCenterThickness(
    input.diameter,
    input.power,
    material.index,
    input.refThickness,
  );
  const average = (thickness.center + thickness.edge) / 2;
  return {
    material,
    thickness: input.power < 0 ? thickness.edge : thickness.center,
    weight: volumeWeight(input.diameter, average, material.density),
  };
}

/** Разница толщины/веса/цены между двумя индексами — готовый аргумент для клиента. */
export function sellUpComparison(input: SellUpInput): SellUpResult {
  const a = sellUpVariant(input, input.indexA);
  const b = sellUpVariant(input, input.indexB);
  return {
    a,
    b,
    thicknessDiff: a.thickness - b.thickness,
    weightDiffPair: (a.weight - b.weight) * 2,
    priceDiff: input.priceB - input.priceA,
  };
}

// --- 15. Проверка заказа (RX) -------------------------------------------------

/** Пороги проверок из прототипа. */
export const RX_LIMITS = {
  /** Максимальная децентрация на глаз, мм. */
  decentration: 4,
  /** Максимальная толщина по краю (центру для плюса), мм. */
  thickness: 5,
  /** Максимальная индуцированная призма, Δ. */
  prism: 2,
  /** Максимальный вертикальный дисбаланс, Δ. */
  imbalance: 1.5,
} as const;

/** Опорная толщина, от которой прототип считает край в сводной проверке, мм. */
export const RX_REFERENCE_THICKNESS = 1.2;
/** Точка чтения ниже оптического центра при оценке дисбаланса, мм. */
export const RX_READING_DROP = 10;

export type RxCheckId = 'diameter' | 'decentration' | 'thickness' | 'prism' | 'imbalance';

export interface RxCheck {
  id: RxCheckId;
  ok: boolean;
  /** Фактическое значение (для `diameter` — выбранный диаметр). */
  value: number;
  /** Порог (для `diameter` — требуемый минимальный диаметр). */
  limit: number;
}

export interface RxValidatorInput {
  /** Сфера OD, дптр. */
  odPower: number;
  /** Сфера OS, дптр. */
  osPower: number;
  /** Цилиндр (общий для оценки), дптр. */
  cyl: number;
  /** A — ширина проёма оправы, мм. */
  a: number;
  /** DBL — переносица, мм. */
  dbl: number;
  /** PD пациента, мм. */
  pd: number;
  /** Выбранный диаметр заготовки, мм. */
  diameter: number;
  /** Запас на обработку, мм. */
  margin: number;
  /** Индекс линзы. */
  index: number;
}

export interface RxValidatorResult {
  /** Требуемый минимальный диаметр заготовки, мм. */
  minDiameter: number;
  decentration: number;
  /** Средний сфероэквивалент по двум глазам, дптр. */
  averageSe: number;
  /** Значимая толщина: край для минуса, центр для плюса, мм. */
  thickness: number;
  isMinus: boolean;
  prism: number;
  imbalance: number;
  checks: RxCheck[];
  allOk: boolean;
}

/**
 * Сводная проверка заказа: последовательно прогоняет расчёты из калькуляторов
 * выше и сравнивает с порогами. Логика и пороги — из прототипа; арифметика
 * толщины и дисбаланса наследует исправления D1/D4.
 */
export function validateRx(input: RxValidatorInput): RxValidatorResult {
  // PD бинокулярный — в монокулярное соглашение переводим делением пополам (D6).
  const decentration = Math.abs(decentrationPerEye(input.a, input.dbl, input.pd / 2));
  const minDiameter = input.a + 2 * decentration + 2 * input.margin;

  const averageSe = (input.odPower + input.osPower) / 2 + input.cyl / 2;
  const isMinus = averageSe < 0;
  const thicknessPair = edgeCenterThickness(
    input.diameter,
    averageSe,
    input.index,
    RX_REFERENCE_THICKNESS,
  );
  const thickness = isMinus ? thicknessPair.edge : thicknessPair.center;

  const prism = prismByPrentice(averageSe, decentration);
  const { difference: imbalance } = verticalImbalance({
    odPower: input.odPower,
    osPower: input.osPower,
    readingDrop: RX_READING_DROP,
  });

  const checks: RxCheck[] = [
    {
      id: 'diameter',
      ok: input.diameter >= minDiameter,
      value: input.diameter,
      limit: minDiameter,
    },
    {
      id: 'decentration',
      ok: decentration <= RX_LIMITS.decentration,
      value: decentration,
      limit: RX_LIMITS.decentration,
    },
    {
      id: 'thickness',
      ok: thickness <= RX_LIMITS.thickness,
      value: thickness,
      limit: RX_LIMITS.thickness,
    },
    { id: 'prism', ok: prism <= RX_LIMITS.prism, value: prism, limit: RX_LIMITS.prism },
    {
      id: 'imbalance',
      ok: imbalance < RX_LIMITS.imbalance,
      value: imbalance,
      limit: RX_LIMITS.imbalance,
    },
  ];

  return {
    minDiameter,
    decentration,
    averageSe,
    thickness,
    isMinus,
    prism,
    imbalance,
    checks,
    allOk: checks.every((check) => check.ok),
  };
}
